import { Reference } from '@approvalmax/types';
import { CheckboxEditor, DropdownEditor, Field, Popup, TextEditor } from '@approvalmax/ui';
import { Box, Divider, Flex, Spacing, Text } from '@approvalmax/ui/src/components';
import { constants, selectors, statics } from 'modules/common';
import { domain, State } from 'modules/data';
import { PlainDataProvider } from 'modules/data-providers';
import { useSelector } from 'modules/react-redux';
import {
    EditableQBooksCounterpartyAddress,
    EditableQBooksCustomer,
    EditableQBooksVendor,
    isQBooksCustomer,
    isQBooksVendor,
} from 'pages/requestForm/data/qbooks/EditableQBooksCounterparty';
import { useGetRecognizedOcrAttachment } from 'pages/requestForm/hooks/useGetRecognizedOcrAttachment';
import { useOcrData } from 'pages/requestForm/hooks/useOcrData';
import { memo, useCallback, useState } from 'react';
import bemFactory from 'react-bem-factory';
import { useDispatch } from 'react-redux';
import { useMount } from 'react-use';
import { notificationService } from 'services/notification';

import { createQBooksCustomer, createQBooksVendor } from '../../../actions';
import { QBooksCustomerCreationPopupData } from '../../../reducers/page/activePopup/qBooksCustomerCreationPopupReducer';
import { QBooksContactCreationPopupData } from '../../../reducers/page/activePopup/qBooksVendorCreationPopupReducer';
import { getActivePopup, getRequest, isLoading } from '../../../selectors/pageSelectors';
import { getQBooksContext, isValidQBOVendorName } from '../../../selectors/qbooks';
import { QBooksCounterpartyAddress } from './components/QBooksCounterpartyAddress/QBooksCounterpartyAddress';
import { checkIsDisplayNameInvalid } from './QBooksCounterpartyCreationPopup.helpers';
import { messages } from './QBooksCounterpartyCreationPopup.messages';
import { Root } from './QBooksCounterpartyCreationPopup.styles';

const { qBooksConstants } = constants;

const qa = bemFactory.qa('reqf-qbooks-vendor-creation-popup');

export const QBooksCounterpartyCreationPopup = memo(() => {
    const qbooksContext = useSelector(getQBooksContext);
    const request = useSelector(getRequest);
    const isSalesInvoice = request.integrationCode === domain.IntegrationCode.QBooksInvoice;

    const counterpartyName = isSalesInvoice ? messages.customer : messages.vendor;

    const loading = useSelector((state: State) => isLoading(state));
    const initContact = useSelector((state: State) =>
        isSalesInvoice
            ? getActivePopup<QBooksCustomerCreationPopupData>(state).initCustomer
            : getActivePopup<QBooksContactCreationPopupData>(state).initVendor
    );
    const dispatch = useDispatch();
    const [counterparty, changeCounterparty] = useState(initContact);
    const [showErrors, changeShowErrorsState] = useState(false);
    const [sameAsBillingAddress, setSameAsBillingAddress] = useState(isSalesInvoice);

    const ocrAttachment = useGetRecognizedOcrAttachment(request);

    const { data: ocrData } = useOcrData(request, ocrAttachment?.id);

    const changeProperty = useCallback(
        (name: keyof EditableQBooksVendor | keyof EditableQBooksCustomer) => {
            return <T,>(value: T) => {
                changeCounterparty((state) => ({ ...state, [name]: value }));
            };
        },
        [changeCounterparty]
    );

    const changeBillingAddress = useCallback(
        (name: keyof EditableQBooksCounterpartyAddress) => {
            return <T,>(value: T) => {
                changeProperty('billingAddress')({ ...counterparty.billingAddress, [name]: value });

                if (sameAsBillingAddress && 'shippingAddress' in counterparty) {
                    changeProperty('shippingAddress')({ ...counterparty.shippingAddress, [name]: value });
                }
            };
        },
        [changeProperty, counterparty, sameAsBillingAddress]
    );

    const changeShippingAddress = useCallback(
        (name: keyof EditableQBooksCounterpartyAddress) => {
            return <T,>(value: T) => {
                if ('shippingAddress' in counterparty) {
                    changeProperty('shippingAddress')({ ...counterparty.shippingAddress, [name]: value });
                }
            };
        },
        [changeProperty, counterparty]
    );

    const changePhone = useCallback(
        (name: keyof EditableQBooksVendor) => {
            return (value: string) => {
                const isValidPhone = value ? selectors.ui.isValidPhone(value) : true;

                if (isValidPhone) {
                    changeCounterparty((state) => ({ ...state, [name]: value }));
                }
            };
        },
        [changeCounterparty]
    );

    const onSameAsBillingAddressChange = useCallback((value: boolean) => {
        setSameAsBillingAddress(value);

        changeCounterparty((prevState) => ({
            ...prevState,
            shippingAddress: prevState.billingAddress,
        }));
    }, []);

    const onDone = useCallback(() => {
        changeShowErrorsState(true);

        if (!counterparty.displayName) {
            notificationService.showErrorToast(messages.validationErrorText);

            return;
        }

        if (counterparty.displayName && !isValidQBOVendorName(counterparty.displayName)) {
            notificationService.showErrorToast(
                messages.invalidDisplayNameErrorText({ counterparty: counterpartyName })
            );

            return;
        }

        if (counterparty.displayName && /\p{Extended_Pictographic}/u.test(counterparty.displayName)) {
            notificationService.showErrorToast(messages.noEmojiInContactName);

            return;
        }

        if (counterparty.displayName && counterparty.displayName.length > qBooksConstants.DISPLAY_NAME_MAX_LENGTH) {
            notificationService.showErrorToast(
                messages.displayNameLengthErrorText({
                    length: qBooksConstants.DISPLAY_NAME_MAX_LENGTH,
                })
            );

            return;
        }

        isQBooksCustomer(counterparty)
            ? dispatch(createQBooksCustomer(counterparty))
            : dispatch(createQBooksVendor(counterparty));
    }, [counterparty, dispatch, counterpartyName]);

    useMount(() => {
        const defaultCurrency = statics.currency.currencyReferences.find(
            (currency) => currency.id === request.companyCurrency
        );

        const processedInitialContact: EditableQBooksCustomer | EditableQBooksVendor = {
            ...initContact,
            displayName: ocrData?.document?.vendor?.name || initContact.displayName,
            currency: ocrData?.document?.currencyCode
                ? {
                      id: ocrData.document?.currencyCode,
                      text:
                          statics.currency.currencyReferences.find(
                              (currency) => currency.id === ocrData.document?.currencyCode
                          )?.text || '',
                  }
                : defaultCurrency,
            billingAddress: {
                ...initContact.billingAddress,
                street: ocrData?.document?.vendor?.address?.street || initContact.billingAddress.street,
                city: ocrData?.document?.vendor?.address?.city || initContact.billingAddress.city,
                state: ocrData?.document?.vendor?.address?.region || initContact.billingAddress.state,
                country: ocrData?.document?.vendor?.address?.country || initContact.billingAddress.country,
                postalCode: ocrData?.document?.vendor?.address?.postalCode || initContact.billingAddress.postalCode,
            },
        };

        changeCounterparty(processedInitialContact);
    });

    const getCurrencyButtonText = (item: Reference | null): string => {
        return (item && statics.currency.findCurrencyShortText(item.id)) || '';
    };

    const isInvalidDisplayName = checkIsDisplayNameInvalid(counterparty.displayName);

    const isInvalidPhone = !!(
        counterparty.primaryPhone &&
        (!counterparty.primaryPhone?.trim() ||
            counterparty.primaryPhone.length > qBooksConstants.PHONE_MAX_LENGTH ||
            !selectors.ui.isValidPhone(counterparty.primaryPhone))
    );

    const isInvalidMobilePhone = !!(
        counterparty.mobilePhone &&
        (!counterparty.mobilePhone?.trim() ||
            counterparty.mobilePhone.length > qBooksConstants.MOBILE_MAX_LENGTH ||
            !selectors.ui.isValidPhone(counterparty.mobilePhone))
    );

    const isInvalidFax = !!(
        counterparty.fax &&
        (!counterparty.fax?.trim() ||
            counterparty.fax.length > qBooksConstants.FAX_MAX_LENGTH ||
            !selectors.ui.isValidPhone(counterparty.fax))
    );

    return (
        <Root
            qa={qa()}
            title={messages.popupTitle({ counterparty: counterpartyName })}
            buttons={
                <Popup.DefaultContent.Button
                    qa={qa('done-button')}
                    disabled={loading}
                    execute={onDone}
                    title={messages.doneButtonTitle({ counterparty: counterpartyName })}
                >
                    {messages.doneButtonText}
                </Popup.DefaultContent.Button>
            }
        >
            <>
                <Divider />

                <Flex overflowY='auto' block>
                    <Box spacing='32 60 40'>
                        <Text font='headline' fontSize='xsmall' uppercase color='midnight90' spacing='0 0 20'>
                            {messages.contactInformationHeader({ counterparty: counterpartyName })}
                        </Text>

                        <Flex container>
                            <Flex size={12} block>
                                <Field title={messages.displayNameAs} required>
                                    <TextEditor
                                        focusOnMount
                                        qa={qa('display-name-as')}
                                        value={counterparty.displayName}
                                        maxLength={qBooksConstants.DISPLAY_NAME_MAX_LENGTH}
                                        onChange={changeProperty('displayName')}
                                        placeholder={messages.displayNameAsPlaceholder}
                                        invalid={showErrors && isInvalidDisplayName}
                                    />
                                </Field>
                            </Flex>

                            <Flex size={12} block>
                                <Field title={messages.companyName}>
                                    <TextEditor
                                        qa={qa('company-name')}
                                        value={counterparty.companyName}
                                        maxLength={qBooksConstants.COMPANY_NAME_MAX_LENGTH}
                                        onChange={changeProperty('companyName')}
                                        placeholder={messages.companyNamePlaceholder}
                                    />
                                </Field>
                            </Flex>
                        </Flex>

                        <Spacing height={12} />

                        <Flex container>
                            <Flex size={2} minWidth={66} block>
                                <Field title={messages.title}>
                                    <TextEditor
                                        qa={qa('title')}
                                        value={counterparty.title}
                                        maxLength={qBooksConstants.TITLE_MAX_LENGTH}
                                        onChange={changeProperty('title')}
                                        placeholder={messages.titlePlaceholder}
                                    />
                                </Field>
                            </Flex>

                            <Flex size={0} block>
                                <Field title={messages.givenName}>
                                    <TextEditor
                                        qa={qa('given-name')}
                                        value={counterparty.givenName}
                                        maxLength={qBooksConstants.FIRST_NAME_MAX_LENGTH}
                                        onChange={changeProperty('givenName')}
                                        placeholder={messages.givenNamePlaceholder}
                                    />
                                </Field>
                            </Flex>

                            <Flex size={0} block>
                                <Field title={messages.middleName}>
                                    <TextEditor
                                        qa={qa('middle-name')}
                                        value={counterparty.middleName}
                                        maxLength={qBooksConstants.MIDDLE_NAME_MAX_LENGTH}
                                        onChange={changeProperty('middleName')}
                                        placeholder={messages.middleNamePlaceholder}
                                    />
                                </Field>
                            </Flex>

                            <Flex size={0} block>
                                <Field title={messages.familyName}>
                                    <TextEditor
                                        qa={qa('family-name')}
                                        value={counterparty.familyName}
                                        maxLength={qBooksConstants.LAST_NAME_MAX_LENGTH}
                                        onChange={changeProperty('familyName')}
                                        placeholder={messages.familyNamePlaceholder}
                                    />
                                </Field>
                            </Flex>

                            <Flex size={2} minWidth={66} block>
                                <Field title={messages.suffix}>
                                    <TextEditor
                                        qa={qa('suffix')}
                                        value={counterparty.suffix}
                                        maxLength={qBooksConstants.SUFFIX_MAX_LENGTH}
                                        onChange={changeProperty('suffix')}
                                        placeholder={messages.suffixPlaceholder}
                                    />
                                </Field>
                            </Flex>
                        </Flex>

                        <Spacing height={12} />

                        <Flex container>
                            <Flex size={12} block>
                                <Field title={messages.email}>
                                    <TextEditor
                                        qa={qa('email')}
                                        value={counterparty.email}
                                        maxLength={qBooksConstants.EMAIL_ADDRESS_MAX_LENGTH}
                                        type='email'
                                        onChange={changeProperty('email')}
                                        placeholder={messages.emailPlaceholder}
                                    />
                                </Field>
                            </Flex>

                            <Flex size={12} block>
                                <Field title={messages.website}>
                                    <TextEditor
                                        qa={qa('website')}
                                        value={counterparty.website}
                                        maxLength={qBooksConstants.WEBSITE_MAX_LENGTH}
                                        type='url'
                                        onChange={changeProperty('website')}
                                        placeholder={messages.websitePlaceholder}
                                        invalid={
                                            showErrors &&
                                            !!counterparty.website &&
                                            !selectors.ui.isValidWebsite(counterparty.website)
                                        }
                                    />
                                </Field>
                            </Flex>
                        </Flex>

                        <Spacing height={12} />

                        <Flex container>
                            <Flex size={12} block>
                                <Field title={messages.primaryPhone}>
                                    <TextEditor
                                        qa={qa('primary-phone')}
                                        value={counterparty.primaryPhone}
                                        maxLength={qBooksConstants.PHONE_MAX_LENGTH}
                                        type='tel'
                                        onChange={changePhone('primaryPhone')}
                                        placeholder={messages.primaryPhonePlaceholder}
                                        invalid={showErrors && isInvalidPhone}
                                    />
                                </Field>
                            </Flex>

                            <Flex size={12} block>
                                <Field title={messages.mobilePhone}>
                                    <TextEditor
                                        qa={qa('mobile-phone')}
                                        value={counterparty.mobilePhone}
                                        maxLength={qBooksConstants.MOBILE_MAX_LENGTH}
                                        type='tel'
                                        onChange={changePhone('mobilePhone')}
                                        placeholder={messages.mobilePhonePlaceholder}
                                        invalid={showErrors && isInvalidMobilePhone}
                                    />
                                </Field>
                            </Flex>
                        </Flex>

                        <Spacing height={12} />

                        <Flex container>
                            <Flex size={12} block>
                                <Field title={messages.fax}>
                                    <TextEditor
                                        qa={qa('fax')}
                                        value={counterparty.fax}
                                        maxLength={qBooksConstants.FAX_MAX_LENGTH}
                                        onChange={changePhone('fax')}
                                        placeholder={messages.faxPlaceholder}
                                        invalid={showErrors && isInvalidFax}
                                    />
                                </Field>
                            </Flex>

                            {qbooksContext.isMultiCurrencyEnabled && (
                                <Flex size={12}>
                                    <Field required title={messages.currency}>
                                        <PlainDataProvider items={statics.currency.currencyReferences}>
                                            <DropdownEditor
                                                value={counterparty.currency}
                                                qa={qa('currency')}
                                                buttonTextSelector={getCurrencyButtonText}
                                                placeholder={messages.currencyPlaceholder}
                                                onChange={changeProperty('currency')}
                                            />
                                        </PlainDataProvider>
                                    </Field>
                                </Flex>
                            )}
                        </Flex>

                        <Spacing height={20} />

                        <Text font='headline' fontSize='xsmall' uppercase color='midnight90' spacing='0 0 20'>
                            {isSalesInvoice ? messages.billingAddressHeader : messages.addressHeader}
                        </Text>

                        <QBooksCounterpartyAddress
                            address={counterparty.billingAddress}
                            changeAddressProperty={changeBillingAddress}
                        />

                        <Spacing height={20} />

                        {isSalesInvoice && (
                            <>
                                <Text font='headline' fontSize='xsmall' uppercase color='midnight90' spacing='0 0 20'>
                                    {messages.shippingAddressHeader}
                                </Text>

                                <CheckboxEditor value={sameAsBillingAddress} onChange={onSameAsBillingAddressChange}>
                                    {messages.sameAsBillingAddress}
                                </CheckboxEditor>

                                {isQBooksCustomer(counterparty) && !sameAsBillingAddress && (
                                    <>
                                        <Spacing height={20} />

                                        <QBooksCounterpartyAddress
                                            address={counterparty.shippingAddress}
                                            changeAddressProperty={changeShippingAddress}
                                        />
                                    </>
                                )}

                                <Spacing height={20} />
                            </>
                        )}

                        {isQBooksVendor(counterparty) && (
                            <Flex container>
                                <Flex size={12}>
                                    <Field title={messages.acctNum}>
                                        <TextEditor
                                            qa={qa('county')}
                                            value={counterparty.acctNum}
                                            maxLength={qBooksConstants.BUSINESS_NUMBER_MAX_LENGTH}
                                            onChange={changeProperty('acctNum')}
                                            placeholder={messages.acctNumPlaceholder}
                                        />
                                    </Field>
                                </Flex>
                            </Flex>
                        )}
                    </Box>
                </Flex>
            </>
        </Root>
    );
});

QBooksCounterpartyCreationPopup.displayName = 'QBooksCounterpartyCreationPopup';
